import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { UserPlus, X, Eye, EyeOff, Camera, RefreshCw, CheckCircle, AlertCircle } from "lucide-react";
import Select from "react-select";
import { getAllDepartments } from "../services/departmentService";
import Cropper from "react-easy-crop";
import Slider from "@mui/material/Slider";
import { getCroppedImg } from "../utils/cropImage";
import { defaultUserImg } from "../assets/defaultUser";
import { fetchUsers as fetchUsersList } from "../actions/UserActions";

// Custom Select Styles
const selectStyles = {
  control: (base) => ({
    ...base,
    borderColor: '#e5e7eb',
    borderRadius: '0.5rem',
    padding: '0.125rem',
    minHeight: '36px',
    '&:hover': {
      borderColor: '#9ca3af'
    }
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#111827' : state.isFocused ? '#f3f4f6' : 'white',
    color: state.isSelected ? 'white' : '#111827',
    '&:active': {
      backgroundColor: '#111827'
    }
  }),
  menu: (base) => ({
    ...base,
    borderRadius: '0.5rem',
    overflow: 'hidden'
  })
};

const AdminUserAdd = ({ isOpen, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { authKey } = useSelector((state) => state.auth);
  const resolvedIsOpen = isOpen !== undefined ? isOpen : true;
  const handleClose = onClose || (() => navigate("/admin/users"));
  const handleSuccess = onSuccess || (() => navigate("/admin/users"));

  /* ================= FORM STATE ================= */
  const [formData, setFormData] = useState({
    ServiceNo: "",
    Name: "",
    MobileNo: "",
    role: "E",
    status: "A",
    department_code: "",
    password: "",
    confirmPassword: "",
    file_path: null,
  });

  const [existingUsers, setExistingUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState("");
  const [message, setMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  /* ================= IMAGE STATE ================= */
  const [previewImage, setPreviewImage] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [profileImageBlob, setProfileImageBlob] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const fileInputRef = useRef(null);

  /* ================= FETCH DEPARTMENTS ================= */
  useEffect(() => {
    const load = async () => {
      try {
        const data = await getAllDepartments();
        setDepartments(data || []);
      } catch (err) {
        console.error("Error loading departments", err);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!resolvedIsOpen) return;

    const fetchUsers = async () => {
      try {
        const usersData = await dispatch(fetchUsersList());
        const users = (usersData || []).map((u) => u.ServiceNo?.toUpperCase());
        setExistingUsers(users);
      } catch (err) {
        console.error("Fetch users error:", err);
      }
    };
    fetchUsers();
  }, [authKey, dispatch, resolvedIsOpen]);

  /* ================= HANDLERS ================= */
  const handleChange = (e) => {
    const { name, value } = e.target;

    if ((name === "ServiceNo" || name === "MobileNo") && !/^\d*$/.test(value)) {
      return;
    }

    if (name === "Name" && !/^[a-zA-Z\s]*$/.test(value)) {
      return;
    }

    setFormData((p) => ({ ...p, [name]: value }));
    setLocalError("");
    setMessage("");
  };

  const handleDeptChange = (option) => {
    setFormData((p) => ({ ...p, department_code: option ? option.value : "" }));
    setLocalError("");
    setMessage("");
  };

  const activeDepartments = (departments || []).filter((d) => {
    const status = String(d?.status || d?.Status || d?.activeStatus || d?.isActive || "")
      .trim()
      .toLowerCase();

    if (typeof d?.isActive === "boolean") return d.isActive;

    return (
      status === "active" ||
      status === "a" ||
      status === "1" ||
      status === "true" ||
      status === "yes"
    );
  });

  const departmentOptions = activeDepartments.map((d) => ({
    value: d.code || d.name || "",
    label: d.name || d.code || "",
  }));

  const validateForm = () => {
    if (!formData.department_code) {
      setLocalError("Please select a department");
      return false;
    }
    if (!/^\d{7}$/.test(formData.ServiceNo)) {
      setLocalError("Service Number must be exactly 7 digits");
      return false;
    }
    if (existingUsers.includes(formData.ServiceNo.toUpperCase())) {
      setLocalError("Service Number already exists");
      return false;
    }
    if (formData.Name.trim().length < 3) {
      setLocalError("Name must be at least 3 characters");
      return false;
    }
    if (!/^\d{10}$/.test(formData.MobileNo)) {
      setLocalError("Mobile Number must be 10 digits");
      return false;
    }
    if (formData.password.length < 8) {
      setLocalError("Password must be at least 8 characters");
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setLocalError("Passwords do not match");
      return false;
    }
    return true;
  };

  /* ================= IMAGE HANDLING ================= */
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) {
      setLocalError("Please select a valid image");
      return;
    }
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      setPreviewImage(reader.result);
      setShowCropModal(true);
      setZoom(1);
      setCrop({ x: 0, y: 0 });
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const applyCrop = async () => {
    try {
      const blob = await getCroppedImg(previewImage, croppedAreaPixels);
      setProfileImageBlob(blob);
      setPreviewImage(URL.createObjectURL(blob));
      setShowCropModal(false);
      setSelectedFile(null);
    } catch {
      setLocalError("Image crop failed");
    }
  };

  const cancelCrop = () => {
    setPreviewImage("");
    setProfileImageBlob(null);
    setSelectedFile(null);
    setShowCropModal(false);
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);

    try {
      const userData = {
        ServiceNo: formData.ServiceNo,
        Name: formData.Name,
        MobileNo: formData.MobileNo,
        role: formData.role,
        status: formData.status,
        password: formData.password,
        created_by: "Admin",
        updated_by: "Admin",
        created_date: new Date().toISOString(),
        updated_date: new Date().toISOString(),
        department_code: formData.department_code,
      };

      const postResp = await axios.post(
        "https://aeroviewtestbackend.dockyardsoftware.com/UserRegister/PostUserDetails",
        [userData],
        {
          headers: { "Content-Type": "application/json", "auth-key": authKey },
        },
      );

      if (postResp?.data?.statusCode !== 200) {
        throw new Error(postResp?.data?.result || "Failed to save user");
      }

      if (profileImageBlob) {
        const formImg = new FormData();
        formImg.append("ServiceNo", formData.ServiceNo);
        const file = new File([profileImageBlob], `${formData.ServiceNo}.jpg`, {
          type: "image/jpeg",
        });
        formImg.append("ImageFile", file);
        await axios.post(
          "https://aeroviewbackend.dockyardsoftware.com/User/Fileupload",
          formImg,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              "auth-key": authKey,
            },
          },
        );
      }

      setMessage("User registered successfully");
      if (onSuccess) onSuccess();
      else handleSuccess();
      setTimeout(() => {
        setMessage("");
        setFormData({
          ServiceNo: "",
          Name: "",
          MobileNo: "",
          role: "E",
          status: "A",
          department_code: "",
          password: "",
          confirmPassword: "",
          file_path: null,
        });
        setPreviewImage("");
        setProfileImageBlob(null);
        handleClose();
      }, 1500);
    } catch (err) {
      console.error(err);
      setLocalError("Failed to register user");
    } finally {
      setLoading(false);
    }
  };

  if (!resolvedIsOpen) return null;

  /* ================= UI ================= */
  return (
    <>
      {/* Modal Overlay */}
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full border border-gray-100 max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in duration-200">
          {/* Header */}
          <div className="sticky top-0 bg-white z-10 flex items-center justify-between p-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-gray-900 rounded-lg">
                <UserPlus size={16} className="text-white" />
              </div>
              <h2 className="text-base font-semibold text-gray-900">
                Add New User
              </h2>
            </div>
            <button
              onClick={handleClose}
              className="p-1 hover:bg-gray-100 rounded-lg transition"
            >
              <X size={16} className="text-gray-400" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4">
            {/* Alerts */}
            {localError && (
              <div className="mb-3 p-2.5 bg-red-50 border border-red-100 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertCircle size={12} className="text-red-600" />
                  <p className="text-xs text-red-600">{localError}</p>
                </div>
              </div>
            )}
            {message && (
              <div className="mb-3 p-2.5 bg-emerald-50 border border-emerald-100 rounded-lg">
                <div className="flex items-center gap-2">
                  <CheckCircle size={12} className="text-emerald-600" />
                  <p className="text-xs text-emerald-600">{message}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-3">
              {/* Profile Image */}
              <div className="flex flex-col items-center gap-2 mb-2">
                <div className="relative group">
                  <img
                    src={previewImage || defaultUserImg}
                    className="w-20 h-20 rounded-full object-cover border-2 border-gray-200 shadow-sm"
                    alt="profile"
                    onError={(e) => {
                      e.currentTarget.src = defaultUserImg;
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute bottom-0 right-0 p-1.5 bg-gray-900 rounded-full hover:bg-gray-800 transition shadow-sm"
                  >
                    <Camera size={10} className="text-white" />
                  </button>
                  {profileImageBlob && (
                    <button
                      type="button"
                      onClick={() => {
                        setPreviewImage("");
                        setProfileImageBlob(null);
                      }}
                      className="absolute -top-1 -right-1 p-0.5 bg-red-500 rounded-full hover:bg-red-600 transition shadow-sm"
                    >
                      <X size={10} className="text-white" />
                    </button>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-xs text-gray-500 hover:text-gray-700 transition"
                >
                  Upload photo
                </button>
              </div>

              {/* Service No & Mobile */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Service No
                  </label>
                  <input
                    name="ServiceNo"
                    value={formData.ServiceNo}
                    onChange={handleChange}
                    placeholder="7 digits"
                    maxLength={7}
                    className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Mobile No
                  </label>
                  <input
                    name="MobileNo"
                    value={formData.MobileNo}
                    onChange={handleChange}
                    placeholder="10 digits"
                    maxLength={10}
                    className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition"
                  />
                </div>
              </div>

              {/* Full Name */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <input
                  name="Name"
                  value={formData.Name}
                  onChange={handleChange}
                  placeholder="Enter full name"
                  className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition"
                />
              </div>

              {/* Role & Status */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Role
                  </label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition bg-white"
                  >
                    <option value="S">Supervisor</option>
                    <option value="E">Employee</option>
                    <option value="EN">Engineer</option>
                    <option value="D">Driver</option>
                    <option value="A">Admin</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition bg-white"
                  >
                    <option value="A">Active</option>
                    <option value="I">Inactive</option>
                  </select>
                </div>
              </div>

              {/* Department */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Department
                </label>
                <Select
                  options={departmentOptions}
                  value={
                    departmentOptions.find(
                      (o) => o.value === formData.department_code,
                    ) || null
                  }
                  onChange={handleDeptChange}
                  placeholder="Select department"
                  isClearable
                  styles={selectStyles}
                />
              </div>

              {/* Password & Confirm */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Min 8 chars"
                      className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition pr-8"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2"
                    >
                      {showPassword ? (
                        <EyeOff size={14} className="text-gray-400" />
                      ) : (
                        <Eye size={14} className="text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Confirm"
                      className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition pr-8"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2"
                    >
                      {showConfirmPassword ? (
                        <EyeOff size={14} className="text-gray-400" />
                      ) : (
                        <Eye size={14} className="text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <RefreshCw size={14} className="animate-spin" />
                      Adding...
                    </>
                  ) : (
                    "Add User"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Crop Modal */}
      {showCropModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="relative bg-white rounded-xl shadow-xl max-w-sm w-full border border-gray-100 overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between p-3 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <div className="p-1 bg-gray-100 rounded-lg">
                  <Camera size={14} className="text-gray-600" />
                </div>
                <h3 className="text-sm font-semibold text-gray-900">
                  Crop Image
                </h3>
              </div>
              <button
                onClick={cancelCrop}
                className="p-1 hover:bg-gray-100 rounded-lg transition"
              >
                <X size={14} className="text-gray-400" />
              </button>
            </div>
            
            <div style={{ position: "relative", width: "100%", height: 280 }}>
              <Cropper
                image={previewImage}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
            
            <div className="p-3 border-t border-gray-100">
              <div className="mb-3">
                <p className="text-xs text-gray-500 mb-1">Zoom</p>
                <Slider
                  value={zoom}
                  min={1}
                  max={3}
                  step={0.1}
                  onChange={(e, v) => setZoom(v)}
                  size="small"
                  sx={{
                    color: '#111827',
                    '& .MuiSlider-thumb': {
                      width: 12,
                      height: 12,
                    }
                  }}
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={cancelCrop}
                  className="flex-1 px-2 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
                >
                  Cancel
                </button>
                <button
                  onClick={applyCrop}
                  className="flex-1 px-2 py-1.5 text-xs font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-lg transition"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminUserAdd;